"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports["default"] = void 0;
exports.getReportDuration = getReportDuration;
exports.getReportParams = getReportParams;
exports.makeReportData = makeReportData;
exports.onPeriodicInterval = onPeriodicInterval;
exports.onPositionChanged = onPositionChanged;
exports.resetPeriodicReportTimer = resetPeriodicReportTimer;
exports.sendBufferReport = sendBufferReport;
exports.sendReport = sendReport;
var _round = _interopRequireDefault(require("lodash/round"));
var _tuner = _interopRequireDefault(require("../tuner"));
var _webApiClientSingleton = _interopRequireDefault(require("../webApiClientSingleton"));
var _Timer = _interopRequireDefault(require("../utils/Timer"));
var msToSec = 1000;
var defaultPeriodicIntervalSeconds = 5 * 60;
var _topicPeriodicIntervalSeconds = 60;
var _qualifiedTunePeriodicIntervalSeconds = 60;
var _periodicIntervalSeconds = defaultPeriodicIntervalSeconds;
var _initialPeriodicIntervalRepeat;
var isFirstPeriodicInterval = true;
var firstPeriodicIntervalTimeoutId;
var pauseStartTime = null;
var contentOffsetMs = 0;
var playbackPosition = 0;
var duration = 0;
var lastReportTime;
var newPosition = 0;
var isLiveSeekStream = false;
var reportTimer = new _Timer["default"]();
function resetOffsets() {
  contentOffsetMs = 0;
  pauseStartTime = null;
}
function clearTimers() {
  reportTimer.clear();
  clearTimeout(firstPeriodicIntervalTimeoutId);
}

/**
 * Pants module.
 * @module loggers/listenTime
 */

/**
 * Periodically send a report
 * @param {Tune} tune - Object containing ID of stream to report on
 * @param {boolean} isQualifiedTuneReport
 */
function onPeriodicInterval(tune) {
  var isQualifiedTuneReport = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  sendReport(tune, 'periodic'); // eslint-disable-line no-use-before-define

  if (isQualifiedTuneReport) {
    _tuner["default"].trigger(_tuner["default"].events.qualifiedTune);
    return;
  }
  resetPeriodicReportTimer(tune); // eslint-disable-line no-use-before-define
}
function setNextPeriodicInterval(tune) {
  var _ref = _tuner["default"].listenTimeConfig || {},
    periodicIntervalSeconds = _ref.periodicIntervalSeconds,
    initialPeriodicIntervalSeconds = _ref.initialPeriodicIntervalSeconds;
  if (_initialPeriodicIntervalRepeat && initialPeriodicIntervalSeconds) {
    _initialPeriodicIntervalRepeat--;
    _periodicIntervalSeconds = initialPeriodicIntervalSeconds;
    return;
  }
  if (periodicIntervalSeconds) {
    _periodicIntervalSeconds = periodicIntervalSeconds;
    return;
  }
  _periodicIntervalSeconds = tune.type === 'topic' ? _topicPeriodicIntervalSeconds : defaultPeriodicIntervalSeconds;
}

/**
 * Resets the periodic reporting time
 * If this is the first time a report is being sent set the periodic reporting interval to 60s.
 * If not the first time we are sending a report:
 *  if the tune type is not a 'topic' set interval set it to 60s
 *  otherwise set it to 300s.
 *
 * Start the timer with the correct timing intervals
 * @param {Tune} tune - Object to reset the timing interval to
 */
function resetPeriodicReportTimer(tune) {
  if (isFirstPeriodicInterval) {
    isFirstPeriodicInterval = false;
    firstPeriodicIntervalTimeoutId = setTimeout(onPeriodicInterval.bind(this, tune, true), _qualifiedTunePeriodicIntervalSeconds * msToSec);
  }
  setNextPeriodicInterval(tune);
  reportTimer.start(onPeriodicInterval.bind(this, tune), _periodicIntervalSeconds * msToSec);
}

/**
 * Create the data to report.
 * @param {string} trigger - The event that was fired in order to create this report
 * @param {number} durationToReport - The duration the stream played since last report was triggered
 */
function makeReportData(trigger, durationToReport) {
  return {
    reports: [{
      trigger: trigger,
      contentOffset: (0, _round["default"])(contentOffsetMs / msToSec),
      listenOffset: 0,
      streamOffset: playbackPosition,
      duration: durationToReport
    }]
  };
}

/**
 * Gets the parameters to construct the API params
 * @param {Tune} tune The tune object
 * @param {string} command Default 'timev3'
 * @returns {{c: string, formats, itemToken, streamGuideId: String, id, render: string, listenId}}
 */
function getReportParams(tune) {
  var command = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'timev3';
  return {
    c: command,
    id: tune.guideId,
    itemToken: tune.itemToken,
    listenId: tune.listenId,
    formats: tune.formats,
    streamGuideId: _tuner["default"].streamPlayer.getCurrentStreamId()
  };
}

/**
 * Calculates the total duration of the report
 * @param {number} reportOffsetTime A UNIX EPOCH timestamp
 * @param {boolean} convertToSeconds Whether or not the duration should be reported in seconds or ms
 * @returns {number}
 */
function getReportDuration(reportOffsetTime) {
  var convertToSeconds = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  var converter = convertToSeconds ? msToSec : 1;
  return (0, _round["default"])((Date.now() - reportOffsetTime) / converter);
}

/**
 * Send a report.
 * Constructs the reporting parameters
 * Makes the report with the event that triggered this action and time spent on stream
 * Resets the last reporting time to now.
 * @param {Tune} tune The object to send for reporting
 * @param {string} trigger The event that triggered this report
 */
function sendReport(tune, trigger) {
  var params = getReportParams(tune);
  var durationToReport = getReportDuration(lastReportTime, true);
  var data = makeReportData(trigger, durationToReport);
  _webApiClientSingleton["default"].instance.logging.report(params, data);
  lastReportTime = Date.now();
}

/**
 * Sends a report based on when buffering started
 * @param {Tune} tune The tune object
 * @param {number} bufferStartTime The timestamp of when buffering began
 */
function sendBufferReport(tune, bufferStartTime) {
  var params = getReportParams(tune, 'buffer');
  params.duration = getReportDuration(bufferStartTime);
  _webApiClientSingleton["default"].instance.logging.report(params);
  lastReportTime = Date.now();
}

/**
 * When a position in the stream has changed
 * @param {event} e
 */
function onPositionChanged(e) {
  duration = e.duration;
  isLiveSeekStream = !!e.isLiveSeekStream;

  // When it comes to events that will reset jplayer metrics, such as stop, we still want to
  // preserve the position as it will reflect the stream offset.
  if (e.elapsedSeconds > 0) {
    playbackPosition = e.elapsedSeconds;
  }
}

/**
 * This module is responsible for handling the reporting of listen activities
 * @module listenTimeLogger
 */
var _default = exports["default"] = {
  /**
   * Send a report when the stream has been paused
   * @function logPause
   * @param {Tune} tune The tune object
   */
  logPause: function logPause(tune) {
    clearTimers();
    pauseStartTime = Date.now();
    sendReport(tune, 'pause');
  },
  /**
   * Stop the periodic logging, reset timers and send a report when the stream has been stopped
   * @function logStop
   * @param {Tune} tune The tune object
   */
  logStop: function logStop(tune) {
    if (reportTimer.isRunning()) {
      this.stopPeriodicLogger(sendReport.bind(null, tune, 'stop'));
    }
  },
  /**
   * Stop the periodic logging, reset timers and send a report when the stream has ended
   * @function logEnd
   * @param {Tune} tune The tune object
   */
  logEnd: function logEnd(tune) {
    this.stopPeriodicLogger(sendReport.bind(null, tune, 'end'));
  },
  /**
   * For live seek streams.
   * When a stream has been moved to a new position via the scrubber
   * For live seek streams we need to recalculate the playback position
   * using the current duration before we calculate the contentOffset so
   * that we don't end up with a negative number which doesn't make sense
   * in the case of live seek streams. This is due to the fact that the
   * duration may have changed since the previous playback position was
   * calculated.
   * Reports a `shift_ff` if the newPosition is greater than the current playbackPosition
   * Reports a `shift_rw` if the newPosition is less than the current playbackPosition
   * @function logShift
   * @param {number} newPositionPercent The current percent of stream
   * @param {Tune} tune
   */
  logShift: function logShift(newPositionPercent, tune) {
    clearTimers();
    var pct = 100;
    // For live seek streams we need to recalculate the playback position
    // using the current duration before we calculate the contentOffset so
    // that we don't end up with a negative number which doesn't make sense
    // in the case of live seek streams. This is due to the fact that the
    // duration may have changed since the previous playback position was
    // calculated.
    if (isLiveSeekStream && newPosition) {
      playbackPosition = (0, _round["default"])(newPosition * duration);
    }
    newPosition = newPositionPercent / pct;
    var newPositionOffset = (0, _round["default"])(newPosition * duration);
    var trigger = newPositionOffset > playbackPosition ? 'shift_ff' : 'shift_rw';
    sendReport(tune, trigger);
    resetPeriodicReportTimer(tune);
    contentOffsetMs += (playbackPosition - newPositionOffset) * msToSec;
    playbackPosition = newPositionOffset;
  },
  /**
   * Send a report when the stream fails
   * @function logFailure
   * @param {Tune} tune The tune object to report
   */
  logFailure: function logFailure(tune) {
    this.stopPeriodicLogger(sendReport.bind(null, tune, 'fail'));
  },
  /**
   * Send a buffering report when stream starts to buffer
   * @function logBuffer
   * @param {Tune} tune
   * @param {number} bufferStartTime The timestamp of when buffering starts
   */
  logBuffer: function logBuffer(tune, bufferStartTime) {
    sendBufferReport(tune, bufferStartTime);
  },
  /**
   * Start the timer for periodic logging.
   * Add the onPositionChanged event listener and reset the timer according to the rules
   * in resetPeriodicReportTime.
   * @function startPeriodicLogger
   * @param tune
   */
  startPeriodicLogger: function startPeriodicLogger(tune) {
    _tuner["default"].streamPlayer.on(_tuner["default"].streamPlayer.events.positionChanged, onPositionChanged);
    resetPeriodicReportTimer(tune);
    if (pauseStartTime) {
      contentOffsetMs += Date.now() - pauseStartTime;
      pauseStartTime = null;
    }
  },
  /**
   * Stop the periodic logger.
   * Remove the event listener and call the callback function if provided
   * @function stopPeriodicLogger
   * @param {function} reportFunc Optional callback
   */
  stopPeriodicLogger: function stopPeriodicLogger(reportFunc) {
    clearTimers();
    _tuner["default"].streamPlayer.remove(_tuner["default"].streamPlayer.events.positionChanged, onPositionChanged);
    if (reportFunc) reportFunc();
    resetOffsets();
    // these may not be necessary:
    playbackPosition = 0;
    duration = 0;
    isLiveSeekStream = false;
  },
  /**
   * Set the timer
   * @function setReportStartTimer
   */
  setReportStartTime: function setReportStartTime() {
    var _ref2 = _tuner["default"].listenTimeConfig || {},
      initialPeriodicIntervalRepeat = _ref2.initialPeriodicIntervalRepeat;
    isFirstPeriodicInterval = true;
    _initialPeriodicIntervalRepeat = initialPeriodicIntervalRepeat;
    lastReportTime = Date.now();
  },
  resetOffsets: resetOffsets
};
//# sourceMappingURL=listenTime.js.map